// Data seam: sphinx REST (/api/sphinx/targets) + local adapters.
//
// Decisions:
// - hosts:* → REST /targets. The fixed 10-target matrix IS the hosts list:
//   each target row maps to a `HostListItem` / `HostDetail` (hostId = target
//   id, human-readable name, modelId = target.model, serverCount =
//   serverIds.length). updateHost / updateHostServers PATCH the target's
//   model / serverIds; setTargetEnabled PATCHes `enabled`. There is no
//   create / duplicate / delete — the matrix is fixed.
// - hostConfigsV2:getProjectDefault → derived: the default host config with
//   every known server attached (REST /servers).
// - hostConfigsV2:getSuiteConfig / setSuiteConfig → localStorage keyed by
//   suite id (`sphinx:suiteHostConfig:<suiteId>`).
// - builtInTools/catalog → static empty list (no host-managed built-ins).
import {
  emptyHostConfigInputV2,
  type HostConfigDtoV2,
  type HostConfigInputV2,
} from "@/lib/client-config-v2";
import {
  SPHINX_MODELS,
  TARGET_IDS,
  TARGET_PROVIDER,
  type SphinxModel,
  type TargetId,
} from "@/shared/sphinx";
import { effectiveModels, modelsForClient } from "@/lib/client-models";
import { sphinxApi, type SphinxTarget } from "@/lib/sphinx-api";
import { useSphinxQuery, withInvalidate } from "@/lib/use-sphinx-query";
import type { DataClient } from "./client";
import { useLocalValue, writeLocal } from "./local-store";
import { DEFAULT_PROJECT_ID } from "./project";

// ---- target → host mapping -------------------------------------------------

export const TARGET_NAMES: Record<TargetId, string> = {
  "claude-desktop": "Claude Desktop",
  "claude-cowork": "Claude Cowork",
  "claude-code": "Claude Code",
  "claude-code-desktop": "Claude Code Desktop",
  "claude-web": "claude.ai",
  "chatgpt-desktop": "ChatGPT Desktop",
  "chatgpt-web": "ChatGPT Web",
  "codex-desktop": "Codex Desktop",
  "anthropic-api": "Anthropic API",
  "openai-api": "OpenAI API",
};

export const targetDisplayName = (id: string) =>
  (TARGET_NAMES as Record<string, string>)[id] ?? id;

/**
 * Provider family each target executes against — re-exported from the shared
 * vocabulary so the page, the Playground and the server's `modelOverridesFor`
 * all read one table. The two direct-API clients are NOT "any": the Anthropic
 * Messages API cannot run a GPT model.
 */
export const TARGET_PROVIDERS: Record<TargetId, "anthropic" | "openai"> =
  TARGET_PROVIDER;

/**
 * Model a client runs when no per-client override is set: Opus 5 for the
 * Anthropic family (incl. the direct Anthropic API), Luna for the OpenAI
 * family (incl. the direct OpenAI API). Mirrors the server's
 * `modelForTarget` fallback (server/queue/enqueue.ts), so what the UI shows
 * for an unset client is what a run actually uses.
 */
export const DEFAULT_TARGET_MODELS: Record<TargetId, SphinxModel> = {
  "claude-desktop": "claude-opus-5",
  "claude-cowork": "claude-opus-5",
  "claude-code": "claude-opus-5",
  "claude-code-desktop": "claude-opus-5",
  "claude-web": "claude-opus-5",
  "anthropic-api": "claude-opus-5",
  "chatgpt-desktop": "gpt-5.6-luna",
  "chatgpt-web": "gpt-5.6-luna",
  "codex-desktop": "gpt-5.6-luna",
  "openai-api": "gpt-5.6-luna",
};

export const defaultModelForTarget = (id: string): SphinxModel | null =>
  (DEFAULT_TARGET_MODELS as Record<string, SphinxModel>)[id] ?? null;

/** Connector (server row, by name) a client attaches when it has none. */
export const DEFAULT_CONNECTOR_NAME = "yarn-dev-harness";

/**
 * Models a client may run. Lives in `@/lib/client-models` (pure, shared with
 * the Playground picker and the Evaluate wizard); re-exported under the name
 * this seam's callers already use.
 */
export const modelsForTarget = modelsForClient;

export { effectiveModels };

/** Targets in canonical matrix order, by id. */
export const sortTargetIds = <T extends { hostId: string }>(rows: T[]): T[] =>
  [...rows].sort(
    (a, b) =>
      (TARGET_IDS as readonly string[]).indexOf(a.hostId) -
      (TARGET_IDS as readonly string[]).indexOf(b.hostId)
  );

/** `HostListItem` (hooks/useClients.ts) plus the raw target fields. */
export type TargetHostListItem = {
  hostId: string;
  name: string;
  hostConfigId: string;
  modelId: string;
  serverCount: number;
  ownerScope?: undefined;
  hasComputer: false;
  createdAt: number;
  updatedAt: number;
  // sphinx extras
  id: TargetId;
  enabled: boolean;
  model: SphinxModel | null;
  /** Every model this client runs (`targets.models`); empty = just `model`. */
  models: SphinxModel[];
  serverIds: string[];
};

export function toHostListItem(t: SphinxTarget): TargetHostListItem {
  // A null model means "no override": show (and run with) the family default.
  const model = t.model ?? defaultModelForTarget(t.id);
  return {
    hostId: t.id,
    name: targetDisplayName(t.id),
    hostConfigId: t.id,
    modelId: model ?? "",
    serverCount: t.serverIds?.length ?? 0,
    hasComputer: false,
    createdAt: 0,
    updatedAt: 0,
    id: t.id,
    enabled: t.enabled,
    model,
    models: t.models ?? [],
    serverIds: t.serverIds ?? [],
  };
}

function toHostConfigDto(
  id: string,
  input: Partial<HostConfigInputV2>
): HostConfigDtoV2 {
  const base = emptyHostConfigInputV2(input);
  return {
    ...(base as unknown as Omit<HostConfigDtoV2, "id" | "schemaVersion">),
    id,
    schemaVersion: 2,
    serverConnectionOverrides: {},
  } as HostConfigDtoV2;
}

export type TargetHostDetail = {
  hostId: string;
  name: string;
  config: HostConfigDtoV2;
  ownerScope?: undefined;
  // sphinx extras
  target: SphinxTarget;
};

export function toHostDetail(t: SphinxTarget): TargetHostDetail {
  return {
    hostId: t.id,
    name: targetDisplayName(t.id),
    config: toHostConfigDto(t.id, {
      modelId: t.model ?? defaultModelForTarget(t.id) ?? "",
      serverIds: t.serverIds ?? [],
      ...((t.config ?? {}) as Partial<HostConfigInputV2>),
    }),
    target: t,
  };
}

const listTargets = () => sphinxApi.targets.list();
const listHosts = async () =>
  sortTargetIds((await listTargets()).map(toHostListItem));

// ---- hosts:* ----------------------------------------------------------------

/** hosts:listHosts (reactive) */
export function useHostsList(args: { projectId: string } | "skip") {
  return useSphinxQuery(args === "skip" ? null : "targets:list", listHosts) as
    | TargetHostListItem[]
    | undefined;
}

/** hosts:listHosts (one-shot) */
export function fetchHosts(_client: DataClient, _args: { projectId: string }) {
  return listHosts();
}

/** hosts:getHost */
export function useHostDetail(args: { hostId: string } | "skip") {
  const hostId = args === "skip" ? null : args.hostId;
  return useSphinxQuery(
    hostId ? `targets:get:${hostId}` : null,
    async () => {
      const row = (await listTargets()).find((t) => t.id === hostId);
      return row ? toHostDetail(row) : null;
    }
  ) as TargetHostDetail | null | undefined;
}

const isSphinxModel = (m: string | null | undefined): m is SphinxModel =>
  !!m && (SPHINX_MODELS as readonly string[]).includes(m);

const updateHost = withInvalidate(
  async (args: {
    hostId: string;
    name?: string;
    input?: HostConfigInputV2;
  }): Promise<{ hostId: string; hostConfigId: string }> => {
    if (args.input) {
      await sphinxApi.targets.patch(args.hostId, {
        serverIds: args.input.serverIds,
        ...(isSphinxModel(args.input.modelId)
          ? { model: args.input.modelId }
          : {}),
      });
    }
    return { hostId: args.hostId, hostConfigId: args.hostId };
  }
);

const updateHostServers = withInvalidate(
  async (args: {
    hostId: string;
    serverIds: string[];
    optionalServerIds?: string[];
  }): Promise<{ hostId: string; hostConfigId: string }> => {
    await sphinxApi.targets.patch(args.hostId, { serverIds: args.serverIds });
    return { hostId: args.hostId, hostConfigId: args.hostId };
  }
);

/** Id of the `yarn-dev` server row, or none when it is not seeded. */
async function defaultConnectorIds(): Promise<string[]> {
  const rows = await sphinxApi.servers.list();
  const row = rows.find(
    (r) => ((r.config as { name?: string } | null)?.name || r.name) === DEFAULT_CONNECTOR_NAME
  );
  return row ? [row.id] : [];
}

/**
 * Turning a client on also fills in what it needs to run: the family default
 * model when none is set, and the `yarn-dev` connector when it has none. An
 * existing override is never touched; turning off only flips `enabled`.
 */
const setTargetEnabled = withInvalidate(
  async (args: { hostId: string; enabled: boolean }) => {
    if (!args.enabled) {
      await sphinxApi.targets.patch(args.hostId, { enabled: false });
      return;
    }
    const row = (await listTargets()).find((t) => t.id === args.hostId);
    const patch: {
      enabled: boolean;
      model?: SphinxModel;
      models?: SphinxModel[];
      serverIds?: string[];
    } = { enabled: true };
    if (!row?.model) {
      const model = defaultModelForTarget(args.hostId);
      if (model) patch.model = model;
    }
    if (!row?.models?.length) {
      // `models` is what a run expands over; seed it from whatever single
      // model the client already had, else the family default.
      const model = row?.model ?? defaultModelForTarget(args.hostId);
      if (model) patch.models = [model];
    }
    if (!row?.serverIds?.length) {
      const serverIds = await defaultConnectorIds();
      if (serverIds.length) patch.serverIds = serverIds;
    }
    await sphinxApi.targets.patch(args.hostId, patch);
  }
);

const setTargetModel = withInvalidate(
  async (args: { hostId: string; model: SphinxModel }) => {
    await sphinxApi.targets.patch(args.hostId, {
      model: args.model,
      models: [args.model],
    });
  }
);

/**
 * Every model this client runs. A run expands to case × client × model, so two
 * entries here means two iterations per case for this client.
 *
 * `model` (the legacy single-model column) is written as the FIRST entry, so a
 * reader that predates `targets.models` — migration 012 — still sees a model
 * this client can actually run rather than a stale one. Picks outside the
 * client's provider family are dropped, and an empty list is refused: it would
 * read as "no models" while the server quietly ran the family default.
 */
const setTargetModels = withInvalidate(
  async (args: { hostId: string; models: SphinxModel[] }) => {
    const allowed = modelsForTarget(args.hostId);
    const models = args.models.filter((m) => allowed.includes(m));
    if (models.length === 0) {
      throw new Error("A client needs at least one model");
    }
    await sphinxApi.targets.patch(args.hostId, {
      models,
      model: models[0],
    });
  }
);

const HOST_MUTATIONS = {
  updateHost,
  updateHostServers,
  setTargetEnabled,
  setTargetModel,
  setTargetModels,
};

export function useHostMutations() {
  return HOST_MUTATIONS;
}

// ---- hostConfigsV2:* ---------------------------------------------------------

const PROJECT_DEFAULT_HOST_ID = `${DEFAULT_PROJECT_ID}-host`;

/** hostConfigsV2:getProjectDefault — every server, default model/prompt. */
export function useProjectDefaultHostConfig(
  args: { projectId: string } | "skip"
) {
  return useSphinxQuery(
    args === "skip" ? null : "targets:projectDefault",
    async () => {
      const servers = await sphinxApi.servers.list();
      return toHostConfigDto(PROJECT_DEFAULT_HOST_ID, {
        serverIds: servers.map((s) => s.id),
      });
    }
  ) as HostConfigDtoV2 | undefined;
}

const suiteConfigKey = (suiteId: string) => `suiteHostConfig:${suiteId}`;
const NO_SUITE_CONFIG = null;

/** hostConfigsV2:getSuiteConfig */
export function useSuiteHostConfig(args: { suiteId: string } | "skip") {
  return useLocalValue<HostConfigDtoV2 | null>(
    args === "skip" ? null : suiteConfigKey(args.suiteId),
    NO_SUITE_CONFIG
  );
}

const setSuiteHostConfig = async (args: {
  suiteId: string;
  input?: HostConfigInputV2;
  [key: string]: unknown;
}): Promise<{ hostConfigId: string }> => {
  const input =
    args.input ??
    (args as { config?: HostConfigInputV2 }).config ??
    ({} as Partial<HostConfigInputV2>);
  writeLocal(
    suiteConfigKey(args.suiteId),
    toHostConfigDto(`suite-${args.suiteId}`, input)
  );
  return { hostConfigId: `suite-${args.suiteId}` };
};

/** hostConfigsV2:setSuiteConfig */
export function useSetSuiteHostConfigMutation() {
  return setSuiteHostConfig;
}

// ---- builtInTools/catalog:* --------------------------------------------------

const NO_BUILT_IN_TOOLS: never[] = [];

/** builtInTools/catalog:listBuiltInTools */
export function useBuiltInToolCatalogQuery(_args: Record<string, never>) {
  return NO_BUILT_IN_TOOLS;
}
